// Dependencies
import React, { FunctionComponent, useCallback, useState } from 'react'

// Styles
import {
  PricetableCardActionButton,
  PricetableCardHeader,
  PricetableCardInformation,
  PricetableCardInformationKnowMoreLabel,
  PricetableCardListItem,
  PricetableCardListItemLabel,
  PricetableCardListWrapper,
  PricetableCardMainPrice,
  PricetableCardPriceWrapper,
  PricetableCardSubPrice,
  PricetableCardTitle,
  PricetableCardActionButtonLabel,
  PricetableCard,
  PriceTableCardSelectorContainer,
  PriceTableCardSelectorContentWrapper,
  PriceTableCardSelectorSlider,
  PriceTableCardSelectorSliderDot,
  PriceTableCardSelectorSliderWrapper,
  PriceTableCardSelectorIconWrapper,
  PriceTableCardSelectorActionIcons,
  PricetableCardHeaderLabel,
  PricetableCardHeaderBenefitsSummaryWrapper,
  PricetableCardHeaderBenefitsSummaryLabel,
  PricetableCardHeaderBenefitsSummarySeparator,
  PricetableCardSeparator,
  PricetableCardChipsWrapper,
  PricetableCardChipItem,
  PricetableCardChipItemLabel,
  PriceTableIaLabelWrapper,
  PriceTableCourseInformationLabel,
  SeparatorLine,
  TooltipText,
  PriceTableCourseInformationIcon
} from './pricetable-card.styles'

// Components
import { ConditionallyRender } from 'component-library/conditionally-render'
import { Asset } from 'components/utilities/asset'

// Assets
import minus from 'assets/minus.svg'
import check from 'assets/check.svg'
import sparkleBlack from 'assets/sparkle-black.svg'
import sparkleWhite from 'assets/sparkle-white.svg'
import info from 'assets/info.svg'
import chevronRight from 'assets/chevron-right.svg'
import chevronLeft from 'assets/chevron-left.svg'

// Types
import {
  PriceTableCardProps,
  PriceTableCardSelectorProps
} from './pricetable-card.types'
import { CarouselSlideDirections } from 'components/Carousel/carousel.types'

// Utils
import { ternary } from 'utils/ternary'

export const PriceTableCard: FunctionComponent<PriceTableCardProps> = ({
  title,
  buttonLabel,
  strikeThroughPriceLabel,
  additionalStrikeThroughPriceLabel,
  listItems,
  mainPrice,
  variant,
  benefitsSummary,
  chipLabel,
  discount,
  courseInformationLabel,
  virtualAssistantLabel,
  virtualAssistantChipLabel,
  tooltipHelperMessage,
  knowMoreLabel,
  handleActionButtonClick,
  handleKnowMoreClick
}) => {
  const colorByTheme = ternary([
    [variant === 'proenem', '#DF1D3D'],
    [variant === 'promed', '#53A4C6'],
    [variant === 'proenem-secondary', '#571D5D'],
    [true, '#DF1D3D']
  ]) as string

  const chipColor = ternary([
    [variant === 'proenem', '#F6BEC7'],
    [variant === 'promed', '#CEE3EE'],
    [variant === 'proenem-secondary', '#DDD2DF'],
    [true, '#FFDA27']
  ]) as string

  const chipLabelColor = ternary([
    [variant === 'proenem', '#5F061D'],
    [variant === 'promed', '#0F2429'],
    [variant === 'proenem-secondary', '#1F0222'],
    [true, '#0F2429']
  ]) as string

  const headerSeparatorColor = ternary([
    [variant === 'proenem', '#F6BEC7'],
    [variant === 'promed', '#53A4C6'],
    [variant === 'proenem-secondary', '#BCA5BE'],
    [true, '#0F2429']
  ]) as string

  const priceColor = ternary([
    [variant === 'proenem', '#5F061D'],
    [variant === 'promed', '#0F2429'],
    [variant === 'proenem-secondary', '#1F0222'],
    [true, '#0F2429']
  ]) as string

  const buttonLabelColor = ternary([
    [variant === 'proenem', '#fff'],
    [variant === 'promed', '#000'],
    [variant === 'proenem-secondary', '#fff'],
    [true, '#0F2429']
  ]) as string

  return (
    <PricetableCard>
      <PricetableCardChipsWrapper>
        <PricetableCardChipItem color={chipColor}>
          <PricetableCardChipItemLabel color={priceColor}>
            {!!chipLabel ? chipLabel : discount}
          </PricetableCardChipItemLabel>
        </PricetableCardChipItem>
      </PricetableCardChipsWrapper>

      <PricetableCardHeader>
        <PricetableCardHeaderLabel>Turma</PricetableCardHeaderLabel>

        <PricetableCardTitle color={colorByTheme}>{title}</PricetableCardTitle>

        <PricetableCardHeaderBenefitsSummaryWrapper>
          <PricetableCardHeaderBenefitsSummaryLabel color={chipLabelColor}>
            {benefitsSummary}
          </PricetableCardHeaderBenefitsSummaryLabel>

          <PricetableCardHeaderBenefitsSummarySeparator
            color={headerSeparatorColor}
          />
        </PricetableCardHeaderBenefitsSummaryWrapper>
      </PricetableCardHeader>

      <PricetableCardPriceWrapper>
        <PricetableCardSubPrice>
          De <i>{strikeThroughPriceLabel}</i>
          {additionalStrikeThroughPriceLabel}
        </PricetableCardSubPrice>

        <PricetableCardMainPrice color={priceColor}>
          {mainPrice}
        </PricetableCardMainPrice>
      </PricetableCardPriceWrapper>

      <PricetableCardActionButton
        onClick={handleActionButtonClick}
        color={colorByTheme}
      >
        <PricetableCardActionButtonLabel color={buttonLabelColor}>
          {buttonLabel}
        </PricetableCardActionButtonLabel>
      </PricetableCardActionButton>

      <PriceTableIaLabelWrapper>
        <SeparatorLine color={headerSeparatorColor} />

        <PriceTableCourseInformationLabel variant="small">
          <PriceTableCourseInformationIcon src={sparkleBlack} />
          {virtualAssistantChipLabel}
        </PriceTableCourseInformationLabel>

        <SeparatorLine color={headerSeparatorColor} />
      </PriceTableIaLabelWrapper>

      <PriceTableCourseInformationLabel variant="large">
        <PriceTableCourseInformationIcon src={sparkleWhite} />

        {courseInformationLabel}
      </PriceTableCourseInformationLabel>

      <ConditionallyRender
        shouldRender={variant === 'promed'}
        content={
          <PriceTableCourseInformationLabel hasHelperText variant="large">
            <PriceTableCourseInformationIcon src={sparkleWhite} />
            {virtualAssistantLabel}

            <PriceTableCourseInformationIcon src={info} />

            <TooltipText className="tooltip">
              {tooltipHelperMessage}
            </TooltipText>
          </PriceTableCourseInformationLabel>
        }
      />

      <PricetableCardInformationKnowMoreLabel onClick={handleKnowMoreClick}>
        {knowMoreLabel}
      </PricetableCardInformationKnowMoreLabel>

      <PricetableCardSeparator />

      <PricetableCardInformation>
        <PricetableCardListWrapper>
          {listItems.map((listItem, index) => (
            <PricetableCardListItem key={index}>
              <Asset
                source={listItem.disabled ? minus : check}
                size={18}
                color={listItem.disabled ? '#cecdd2' : '#e9ebf1'}
              />

              <PricetableCardListItemLabel isDisabled={listItem.disabled}>
                {listItem.text}
              </PricetableCardListItemLabel>
            </PricetableCardListItem>
          ))}
        </PricetableCardListWrapper>
      </PricetableCardInformation>
    </PricetableCard>
  )
}

export const PriceTableCardSelector: FunctionComponent<
  PriceTableCardSelectorProps
> = ({ content }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [slideDirection, setSlideDirection] =
    useState<CarouselSlideDirections>('right')

  const handlePrevious = () => {
    setSlideDirection('left')
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + content.length) % content.length
    )
  }

  const handleNext = useCallback(() => {
    setSlideDirection('right')
    setCurrentIndex((prevIndex) => (prevIndex + 1) % content.length)
  }, [content.length])

  return (
    <PriceTableCardSelectorContainer>
      <PriceTableCardSelectorSliderWrapper>
        {content.map((item, index) => (
          <PriceTableCardSelectorContentWrapper
            key={index}
            slideDirection={slideDirection}
            isActive={index === currentIndex}
          >
            {item}
          </PriceTableCardSelectorContentWrapper>
        ))}

        <PriceTableCardSelectorActionIcons>
          <PriceTableCardSelectorIconWrapper onClick={handlePrevious}>
            <Asset source={chevronRight} size={20} color="#fff" />
          </PriceTableCardSelectorIconWrapper>

          <PriceTableCardSelectorSlider>
            {content.map((_, index) => (
              <PriceTableCardSelectorSliderDot
                key={index}
                isActive={index === currentIndex}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
          </PriceTableCardSelectorSlider>

          <PriceTableCardSelectorIconWrapper onClick={handleNext}>
            <Asset source={chevronLeft} size={20} color="#fff" />
          </PriceTableCardSelectorIconWrapper>
        </PriceTableCardSelectorActionIcons>
      </PriceTableCardSelectorSliderWrapper>
    </PriceTableCardSelectorContainer>
  )
}
